import React from 'react';
import './BudgetPrintView.css';
import { openPdf } from '../utils/pdfHelpers';

interface BudgetItem {
  id: string;
  name: string;
  qty: number;
  sellingPrice: number;
  type: string;
  originalPrice?: number;
  discountApplied?: string;
  discountAmount?: number;
  optionCode?: string;
}

interface Client {
  id: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  type?: 'individual' | 'company';
  phone?: string;
  email?: string;
}

interface Vehicle {
  id: string;
  make?: string;
  brand?: string;
  model: string;
  year: string;
  licensePlate: string;
}

interface Budget {
  id: string;
  budgetNumber?: string;
  clientId: string;
  vehicleId?: string;
  description?: string;
  totalAmount: number;
  validUntil: string;
  createdAt: string;
  status: string;
  paymentMethod?: string;
  items?: BudgetItem[];
  customDiscountId?: string;
  appliedDiscount?: any;
}

interface BudgetPrintViewProps {
  budget: Budget;
  client: Client;
  vehicle?: Vehicle;
  onClose: () => void;
}

const BudgetPrintView: React.FC<BudgetPrintViewProps> = ({ budget, client, vehicle, onClose }) => {
  console.log('🚀 BudgetPrintView renderizado - budget:', budget);
  console.log('🚀 budget.appliedDiscount:', (budget as any).appliedDiscount);
  
  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    const filename = budget.budgetNumber
      ? `Presupuesto-${budget.budgetNumber}.pdf`
      : `Presupuesto-${budget.id.slice(-6)}.pdf`;
    openPdf({ url: `/api/budgets/${budget.id}/pdf`, filename });
  };

  const calculateTotal = () => {
    if (!budget.items) return budget.totalAmount;
    return budget.items.reduce((total, item) => total + (item.qty * item.sellingPrice), 0);
  };

  const calculateDiscountInfo = () => {
    console.log('🔍 calculateDiscountInfo llamada');
    console.log('🔍 budget:', budget);
    console.log('🔍 budget.appliedDiscount:', (budget as any).appliedDiscount);
    
    // Verificar si hay descuento aplicado en el presupuesto
    if (!(budget as any).appliedDiscount) {
      console.log('❌ No hay appliedDiscount en el presupuesto');
      return { hasDiscount: false, totalDiscount: 0, discountName: '' };
    }
    
    const appliedDiscount = (budget as any).appliedDiscount;
    console.log('✅ AppliedDiscount encontrado:', appliedDiscount);
    
    // Calcular descuento simple: 20% del subtotal base
    const baseItems = budget.items ? budget.items.filter(item => !item.optionCode) : [];
    const baseSubtotal = baseItems.reduce((total, item) => total + (item.qty * item.sellingPrice), 0);
    const discountAmount = (baseSubtotal * 20) / 100; // 20% fijo
    
    console.log('💰 Base items:', baseItems.map(item => ({ name: item.name, type: item.type, price: item.sellingPrice })));
    console.log('💰 Base subtotal:', baseSubtotal);
    console.log('💰 Descuento calculado:', discountAmount);
    
    return { 
      hasDiscount: true, 
      totalDiscount: discountAmount, 
      discountName: appliedDiscount.name || 'Descuento autopista' 
    };
  };

  const getBaseItems = () => {
    if (!budget.items) return [];
    return budget.items.filter(item => !item.optionCode);
  };

  const getOptionItems = () => {
    if (!budget.items) return [];
    return budget.items.filter(item => item.optionCode);
  };

  const clientName = client.type === 'company' 
    ? (client.companyName || 'Empresa') 
    : `${client.firstName || ''} ${client.lastName || ''}`.trim();

  return (
    <div className="budget-print-container">
      {/* Botones de acción - solo visibles en pantalla */}
      <div className="print-actions no-print">
        <button onClick={handlePrint} className="btn print-btn-primary">
          🖨️ Imprimir
        </button>
        <button onClick={handleDownloadPDF} className="btn print-btn-success">
          📄 Descargar PDF
        </button>
        <button onClick={onClose} className="btn print-btn-secondary">
          ❌ Cerrar
        </button>
      </div>

      {/* Contenido del presupuesto */}
      <div className="budget-print-content">
        {/* Encabezado moderno con gradiente */}
        <div className="modern-header">
          <div className="header-gradient">
            <div className="logo-container">
              <div className="logo-circle">
                <div className="logo-inner">
                  <span className="logo-text">ROCAM</span>
                </div>
              </div>
            </div>
            
            <div className="workshop-info-modern">
              <h1 className="workshop-name-modern">ROCAM NEUMÁTICOS</h1>
              <p className="workshop-tagline">Especialistas en Neumáticos y Mecánica</p>
              
              <div className="contact-info">
                <div className="contact-item">
                  <span className="contact-icon">📍</span>
                  <span>Bv. Oroño 620, San Lorenzo, Santa Fe</span>
                </div>
                <div className="contact-item">
                  <span className="contact-icon">📞</span>
                  <span>3476-432564</span>
                </div>
                <div className="contact-item">
                  <span className="contact-icon">💬</span>
                  <span>WhatsApp: 3476-409820</span>
                </div>
              </div>
            </div>
            
            <div className="budget-number-modern">
              <div className="budget-number-badge">
                PRESUPUESTO #{budget.id.toString().padStart(6, '0')}
              </div>
              <div className="budget-date">
                {new Date(budget.createdAt).toLocaleDateString('es-AR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Tarjeta de información del cliente */}
        <div className="client-card">
          <div className="card-shadow"></div>
          <div className="card-content">
            <h2 className="section-title">INFORMACIÓN DEL CLIENTE</h2>
            
            <div className="client-details">
              <div className="detail-row">
                <span className="detail-icon">👤</span>
                <span className="detail-label">Cliente:</span>
                <span className="detail-value">{clientName}</span>
              </div>
              
              {client.phone && (
                <div className="detail-row">
                  <span className="detail-icon">📱</span>
                  <span className="detail-value">{client.phone}</span>
                </div>
              )}
              
              {client.email && (
                <div className="detail-row">
                  <span className="detail-icon">✉️</span>
                  <span className="detail-value">{client.email}</span>
                </div>
              )}
            </div>
            
            {vehicle && (
              <div className="vehicle-details">
                <div className="detail-row">
                  <span className="detail-icon">🚗</span>
                  <span className="detail-label">Vehículo:</span>
                  <span className="detail-value">{vehicle.make || vehicle.brand} {vehicle.model} - {vehicle.year}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-icon">🔢</span>
                  <span className="detail-value">Patente: {vehicle.licensePlate}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tarjeta de validez */}
        <div className="validity-card">
          <div className="validity-gradient">
            <span className="validity-icon">⏰</span>
            <span className="validity-text">
              Válido hasta: {new Date(budget.validUntil).toLocaleDateString('es-AR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
          </div>
        </div>

        {/* Tabla de ítems moderna */}
        <div className="items-section">
          <h2 className="section-title">DETALLE DE TRABAJOS Y REPUESTOS</h2>
          
          {budget.items && budget.items.length > 0 && (
            <div className="items-table-modern">
              <div className="table-header">
                <div className="header-cell">
                  <span className="header-icon">📋</span>
                  <span>Descripción</span>
                </div>
                <div className="header-cell">
                  <span className="header-icon">🔢</span>
                  <span>Cant.</span>
                </div>
                <div className="header-cell">
                  <span className="header-icon">💰</span>
                  <span>Precio Unit.</span>
                </div>
                <div className="header-cell">
                  <span className="header-icon">💵</span>
                  <span>Total</span>
                </div>
              </div>
              
              <div className="table-body">
                {/* Items base */}
                {getBaseItems().map((item, index) => {
                  const itemTotal = item.qty * item.sellingPrice;
                  return (
                    <div key={item.id} className={`table-row ${index % 2 === 0 ? 'row-even' : 'row-odd'}`}>
                      <div className="table-cell">{item.name}</div>
                      <div className="table-cell">{item.qty}</div>
                      <div className="table-cell">${item.sellingPrice.toFixed(2)}</div>
                      <div className="table-cell">${itemTotal.toFixed(2)}</div>
                    </div>
                  );
                })}
              </div>
              
              {/* Subtotal base */}
              {getBaseItems().length > 0 && (
                <div className="table-subtotal">
                  <div className="subtotal-label">Subtotal:</div>
                  <div className="subtotal-amount">
                    ${(() => {
                      const baseTotal = getBaseItems().reduce((total, item) => total + (item.qty * item.sellingPrice), 0);
                      return baseTotal.toFixed(2);
                    })()}
                  </div>
                </div>
              )}
              
              {/* Descuento aplicado a items base */}
              {(() => {
                console.log('🔍 Renderizando descuento...');
                const { hasDiscount, totalDiscount, discountName } = calculateDiscountInfo();
                console.log('🔍 Resultado calculateDiscountInfo:', { hasDiscount, totalDiscount, discountName });
                
                if (hasDiscount) {
                  console.log('✅ Mostrando línea de descuento');
                  return (
                    <div className="table-discount">
                      <div className="discount-label">Descuento: {discountName} (en servicios)</div>
                      <div className="discount-amount">-${totalDiscount.toFixed(2)}</div>
                    </div>
                  );
                } else {
                  console.log('❌ No se muestra descuento - hasDiscount:', hasDiscount);
                }
                return null;
              })()}
              
              {/* Items de opciones */}
              {getOptionItems().length > 0 && (
                <>
                  <div className="table-body">
                    {getOptionItems().map((item, index) => {
                      const itemTotal = item.qty * item.sellingPrice;
                      return (
                        <div key={item.id} className={`table-row ${index % 2 === 0 ? 'row-even' : 'row-odd'}`}>
                          <div className="table-cell">{item.name}</div>
                          <div className="table-cell">{item.qty}</div>
                          <div className="table-cell">${item.sellingPrice.toFixed(2)}</div>
                          <div className="table-cell">${itemTotal.toFixed(2)}</div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
              
              <div className="table-total">
                <div className="total-label">TOTAL:</div>
                <div className="total-amount">${calculateTotal().toFixed(2)}</div>
              </div>
            </div>
          )}
        </div>

        {/* Tarjeta de términos y condiciones */}
        <div className="terms-card">
          <div className="card-shadow"></div>
          <div className="card-content">
            <h2 className="section-title">CONDICIONES Y GARANTÍAS</h2>
            
            <div className="terms-list">
              <div className="term-item">
                <span className="term-icon">⏱️</span>
                <span>Precios válidos por 30 días desde la emisión</span>
              </div>
              <div className="term-item">
                <span className="term-icon">🔧</span>
                <span>Incluye mano de obra y repuestos necesarios</span>
              </div>
              <div className="term-item">
                <span className="term-icon">📋</span>
                <span>Se requiere aprobación previa para comenzar trabajos</span>
              </div>
              <div className="term-item">
                <span className="term-icon">🛡️</span>
                <span>Garantía: 6 meses en repuestos, 3 meses en mano de obra</span>
              </div>
              <div className="term-item">
                <span className="term-icon">💳</span>
                <span>Aceptamos: Efectivo, Transferencia, Tarjetas</span>
              </div>
              <div className="term-item">
                <span className="term-icon">🕒</span>
                <span>Horarios: Lun-Vie 8:00-18:00, Sáb 8:00-12:00</span>
              </div>
              <div className="term-item">
                <span className="term-icon">📞</span>
                <span>Soporte técnico disponible durante el trabajo</span>
              </div>
            </div>
          </div>
        </div>

        {/* Pie de página moderno */}
        <div className="modern-footer">
          <div className="footer-content">
            <div className="footer-left">
              <h3>¿CÓMO APROBAR ESTE PRESUPUESTO?</h3>
              <div className="footer-item">
                <span className="footer-icon">✅</span>
                <span>Responda "APROBAR" por WhatsApp</span>
              </div>
              <div className="footer-item">
                <span className="footer-icon">❓</span>
                <span>Para consultas: "CONSULTA"</span>
              </div>
              <div className="footer-item">
                <span className="footer-icon">📞</span>
                <span>Llámenos: 3476-432564</span>
              </div>
            </div>
            
            <div className="footer-right">
              <p>¡Gracias por confiar en nosotros!</p>
              <p>🚗 Rocam Neumáticos 🚗</p>
              <p>Especialistas en Neumáticos</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetPrintView; 
